import { CloudArrowUpIcon } from '@heroicons/react/24/outline'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import type { ChangeEvent, DragEvent } from 'react'
import { useCallback, useState } from 'react'
import type { Observable, Subject } from 'rxjs'
import { TooltipTrigger } from '../../../../Provider/TooltipManager.tsx'

export interface IFileInputProps {
  onChange$?: Subject<File[]>
  isValid?: boolean
  onReset$?: Observable<void>
  isMultiple: boolean
  fileType: string
  fileAccept: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fileCounter: any
  className?: string
  setStarted: (started: boolean) => void
  disabled?: boolean
  testId?: string
  infoTooltip?: string
}

export const FileInput = ({
  onChange$,
  isValid,
  isMultiple,
  fileType,
  fileCounter,
  fileAccept,
  className,
  setStarted,
  disabled,
  testId,
  infoTooltip
}: IFileInputProps) => {
  const [isDragging, setIsDragging] = useState(false)

  const pushFiles = useCallback(
    (files: File[]) => {
      setStarted(true)
      if (onChange$) onChange$.next(files)
    },
    [onChange$, setStarted]
  )

  const handleOnChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      pushFiles(Array.from(event.target.files || []))
      event.target.value = ''
    },
    [pushFiles]
  )

  const handleDragOver = useCallback((event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    if (!disabled) {
      setIsDragging(true)
    }
  }, [disabled])

  const handleDragLeave = useCallback((event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((event: DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (disabled) return
    pushFiles(Array.from(event.dataTransfer.files || []))
  }, [disabled, pushFiles])
  return (
    <div className={`${className ? className : 'w-full'}`}>
      <label
        className={`relative flex justify-center w-full md:h-32 px-4 transition ${
          disabled === true ? 'bg-gray-300' : 'bg-green-100'
        } border-2 ${isDragging ? 'border-green-500 bg-green-200' : isValid === true ? 'border-green-400' : 'border-gray-400'} border-solid rounded-md appearance-none cursor-pointer hover:border-green-400 focus:outline-hidden`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {infoTooltip && (
          <TooltipTrigger tooltip={infoTooltip} classNames="absolute top-1 right-1">
            {/* Inside the label so drops on it still land; preventDefault keeps a click from opening the file picker. */}
            <span
              data-testid={testId ? `${testId}-info` : undefined}
              aria-label={infoTooltip}
              role="img"
              onClick={(e) => e.preventDefault()}
            >
              <InformationCircleIcon className={`h-4 w-4 ${disabled === true ? 'text-gray-400' : 'text-green-500'}`} />
            </span>
          </TooltipTrigger>
        )}
        <div className="flex items-center space-x-2 flex-row">
          <CloudArrowUpIcon
            type="cloud-upload-icon"
            className={`w-20 h-20 ${disabled === true ? 'stroke-gray-500' : 'stroke-green-400'}`}
          />
          <div className={`${disabled === true ? 'text-gray-500' : 'text-green-600'}`}>
            <p>{fileCounter === 0 ? fileType : fileCounter + ' files'}</p>
            <p className="text-xs">{isDragging ? 'Drop files here' : 'Click or drag files here'}</p>
          </div>
        </div>
        <input
          data-testid={testId}
          type="file"
          name="file_upload"
          disabled={disabled === true}
          className="hidden"
          accept={fileAccept}
          onChange={handleOnChange}
          color={isValid === true ? 'success' : isValid === false ? 'error' : 'primary'}
          multiple={isMultiple}
        />
      </label>
    </div>
  )
}
